import moment, { Moment } from 'moment';

import { HistoryEventKindEnum } from '../../enums/history-event-kind.enum';

/**
 * Événements de la timeline d'historique d'un BusinessObject, renvoyés par
 * l'endpoint générique `GET /<objects>/{id}/history/`.
 *
 * Payload en lecture seule, non persisté : classes légères hors ModelBase
 * (même précédent que `AuthorizedStatus`). Contrat back :
 * b4cash-srv/documentation/architecture/OBJECT_HISTORY_API.md
 */

/** Format d'une date seule ISO 8601 (ex. "2026-08-15") */
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
/** Format d'un datetime ISO 8601 (ex. "2026-07-09T09:05:00Z") */
const ISO_DATETIME_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/** Auteur d'un événement (ou destinataire d'un statut pour `statusUser`) */
export class HistoryEventUser {

    readonly id!: number;
    readonly firstName!: string;
    readonly lastName!: string;
    /** URL présignée de l'avatar (validité 24h), null si l'utilisateur n'en a pas */
    readonly avatar: string | null = null;
    readonly avatarLabelColor!: string;
    readonly system!: boolean;

    constructor(data: any) {
        Object.assign(this, data);
    }

    public get fullName(): string {
        return [this.firstName, this.lastName].filter(part => !!part).join(' ');
    }

    public get initials(): string {
        return [this.firstName, this.lastName]
            .filter(part => !!part)
            .map(part => part.charAt(0).toUpperCase())
            .join('');
    }
}

/** Diff d'un champ modifié : valeurs brutes du back, mise en forme côté front */
export class HistoryEventChange {

    readonly field!: string;
    readonly label!: string;
    readonly old: any = null;
    readonly new: any = null;

    /** Valeurs mises en forme pour affichage (calculées une seule fois) */
    readonly oldDisplay: string | null = null;
    readonly newDisplay: string | null = null;

    constructor(data: any) {
        Object.assign(this, data);
        this.oldDisplay = HistoryEventChange._formatValue(this.old);
        this.newDisplay = HistoryEventChange._formatValue(this.new);
    }

    /**
     * Mise en forme d'une valeur brute : booléens en Oui/Non, dates ISO au
     * format français, le reste stringifié tel quel.
     */
    private static _formatValue(value: any): string | null {
        if (value === null || value === undefined) {
            return null;
        }
        if (typeof value === 'boolean') {
            return value ? 'Oui' : 'Non';
        }
        if (typeof value === 'string') {
            if (ISO_DATETIME_PATTERN.test(value)) {
                const date = moment(value);
                if (date.isValid()) {
                    return date.format('DD/MM/YYYY HH:mm');
                }
            } else if (ISO_DATE_PATTERN.test(value)) {
                const date = moment(value, 'YYYY-MM-DD', true);
                if (date.isValid()) {
                    return date.format('DD/MM/YYYY');
                }
            }
            return value;
        }
        return value.toString();
    }
}

/** Sous-objet concerné par un événement (ligne, taxe…) */
export class HistoryEventScope {

    readonly model!: string;
    readonly id!: number;
    readonly label!: string;

    constructor(data: any) {
        Object.assign(this, data);
    }
}

/** Statut posé ou retiré (événements `statusSet` / `statusRemoved`) */
export class HistoryEventStatus {

    readonly code!: string;
    readonly label!: string;

    constructor(data: any) {
        Object.assign(this, data);
    }
}

export class HistoryEvent {

    readonly date!: Moment;
    readonly user: HistoryEventUser | null = null;
    readonly kind!: string;
    readonly reason: string | null = null;
    readonly scope: HistoryEventScope | null = null;
    readonly status: HistoryEventStatus | null = null;
    readonly statusUser: HistoryEventUser | null = null;
    readonly changes: HistoryEventChange[] = [];

    constructor(data: any) {
        this.date = moment(data?.date);
        this.user = !!data?.user ? new HistoryEventUser(data.user) : null;
        this.kind = data?.kind;
        this.reason = data?.reason || null;
        this.scope = !!data?.scope ? new HistoryEventScope(data.scope) : null;
        this.status = !!data?.status ? new HistoryEventStatus(data.status) : null;
        this.statusUser = !!data?.statusUser ? new HistoryEventUser(data.statusUser) : null;
        this.changes = (data?.changes || []).map((change: any) => new HistoryEventChange(change));
    }

    /** Détails affichables dans la carte dépliée */
    public get hasDetails(): boolean {
        return this.changes.length > 0 || !!this.reason;
    }

    /**
     * Événement d'origine automatique : utilisateur de service.
     * Un événement sans utilisateur est considéré comme humain (arbitrage métier).
     */
    public get isSystem(): boolean {
        return !!this.user?.system;
    }

    public get isStatusEvent(): boolean {
        return this.kind === HistoryEventKindEnum.StatusSet || this.kind === HistoryEventKindEnum.StatusRemoved;
    }
}
